import React, { useMemo } from "react";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { addDays, startOfToday } from "date-fns";
import { useIntl } from "react-intl";

import FrequencyStatus from "@mapmycustomers/shared/enum/frequency/FrequencyStatus";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";

import { formatFriendlyRelativeTime } from "@app/util/formatters";

import styles from "./FrequencyStatusLink.module.scss";

interface Props {
  entity: Company | Deal | Person;
}

const FrequencyStateLink = ({
  entity: { cadenceDaysOut, cadenceStatus, overdueActivitiesCount, upcomingActivity },
}: Props) => {
  const intl = useIntl();

  const text = useMemo(() => {
    if (cadenceStatus === FrequencyStatus.PAST_DUE) {
      if (overdueActivitiesCount > 0) {
        return intl.formatMessage(
          {
            id: "frequency.frequencyStateLink.pastDue",
            defaultMessage:
              "{count, plural, one {Overdue Activity} other {{count} Overdue Activities}}",
            description: "frequency component - frequency State Link- past due",
          },
          { count: overdueActivitiesCount }
        );
      }
      return intl.formatMessage(
        {
          id: "frequency.frequencyStateLink.pastDue.days",
          defaultMessage:
            "Past Due {count, plural, =0 {Today} one {{count} Day} other {{count} Days}}",
          description: "frequency component - frequency State Link- past due",
        },
        { count: cadenceDaysOut }
      );
    }
    if (upcomingActivity?.startAt) {
      return intl.formatMessage(
        {
          id: "frequency.frequencyStateLink.upcomingActivity",
          defaultMessage: "Next Activity {date}",
          description: "frequency component - frequency State Link- Next Activity",
        },
        { date: formatFriendlyRelativeTime(intl, upcomingActivity.startAt) }
      );
    }

    if (cadenceStatus === FrequencyStatus.UPCOMING) {
      return intl.formatMessage(
        {
          id: "frequency.frequencyStateLink.upcoming",
          defaultMessage: "Activity Due {count, select, 0 {Today} other {{date}}}",
          description: "frequency component - frequency State Link- upcoming",
        },
        {
          count: cadenceDaysOut ?? 0,
          date: formatFriendlyRelativeTime(
            intl,
            addDays(startOfToday(), -(cadenceDaysOut ?? 0)).toISOString()
          ),
        }
      );
    }
    return undefined;
  }, [cadenceDaysOut, cadenceStatus, intl, overdueActivitiesCount, upcomingActivity]);

  if (!text) {
    return null;
  }

  return (
    <span
      className={cn(styles.status, {
        [styles.error]: cadenceStatus === FrequencyStatus.PAST_DUE,
        [styles.warning]: cadenceStatus === FrequencyStatus.UPCOMING,
      })}
    >
      <FontAwesomeIcon icon={faExclamationCircle} />
      <span>{text}</span>
      <FontAwesomeIcon icon={faChevronRight} />
    </span>
  );
};

export default FrequencyStateLink;
